/* ==========================================================================
   cl-recettes.js — Recettes et cocktails accordés au produit affiché
   --------------------------------------------------------------------------
   Rapproche la fiche produit de la bibliothèque de recettes de la cave.

   DEUX CRITÈRES D'ACCORD, DANS CET ORDRE
   1. La recette CITE le produit dans son métachamp `recette.produits`.
      Accord certain, écrit par la cave : il passe devant tout.
   2. À défaut, recoupement de mots :
      - cocktail : l'alcool de base de la recette doit être celui de la fiche ;
      - plat : le nom du plat doit figurer dans les accords mets du produit.
      Sans recoupement, rien ne s'affiche. Mieux vaut aucune suggestion
      qu'une suggestion à côté.

   L'index de la bibliothèque est chargé une seule fois par visite (cache
   de session), et seulement quand le visiteur arrive à cette hauteur de
   page : une fiche jamais déroulée jusque-là ne coûte aucune requête.

   Toute recette publiée ensuite entre d'elle-même dans les fiches
   concernées : rien à relier à la main.
   ========================================================================== */
(function () {
  'use strict';

  var BLOG_CUISINE = 'recettes-de-cuisine';
  var BLOG_COCKTAILS = 'recettes-de-cocktails';

  /* Mots trop courants pour dire quoi que ce soit d'un accord. */
  var VIDES = ('avec,pour,dans,les,des,une,uns,sur,ainsi,encore,quelques,notes,'
    + 'belle,ses,son,sa,leur,que,qui,est,sont,aux,par,plus,tout,tous,toute,'
    + 'bien,tres,tres,tout,cette,elle,mais,nos,vos,type,tres,tout,fait,tres,'
    + 'accompagne,accorde,accord,ideal,idealement,egalement,ou,et,de,du,la,le,'
    + 'un,en,au,il,se,ne,ce,sans,plat,plats,gout,gouts,servir,sert,degustation'
  ).split(',');

  /* Accents et ponctuation retirés : les fiches et les recettes ne les
     écrivent pas toujours de la même façon. */
  function normaliser(txt) {
    return (txt || '')
      .toLowerCase()
      /* ⚠️ Les ligatures d'abord : « œ » n'est pas dans [a-z], « bœuf »
         devenait « b uf » et aucun accord au bœuf n'était trouvé. */
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function motsUtiles(txt) {
    var vus = {};
    return normaliser(txt).split(' ').filter(function (m) {
      if (m.length < 5) return false;
      if (VIDES.indexOf(m) !== -1) return false;
      if (vus[m]) return false;
      vus[m] = 1;
      return true;
    });
  }

  /* Mots qui décrivent une CUISSON ou une catégorie, jamais un plat.
     Sans cette liste, « entrecôte grillée » dans les accords faisait
     remonter « Aubergines grillées » sur un Castillon : le rapprochement
     s'accrochait au mode de cuisson au lieu du plat. Ces mots ne valent
     donc rien à eux seuls — il faut au moins un vrai nom de plat. */
  var FAIBLES = ('grille,grillee,grillees,grilles,grillade,grillades,'
    + 'roti,rotie,roties,rotis,braise,braisee,braises,braisees,'
    + 'poele,poelee,poelees,mijote,mijotee,saute,sautee,farci,farcie,'
    + 'cuisson,cuite,cuites,sauce,sauces,viande,viandes,'
    + 'plats,plate,entree,entrees,dessert,desserts,accompagnement,'
    + 'frais,fraiche,fraiches,jeune,jeunes,affine,affines,affinee,affinees,'
    + 'recette,recettes,maison,facile,minute,minutes,'
    + 'rouge,rouges,blanc,blanche,blancs,noire,noirs,douce,doux,'
    + 'legere,legers,legeres,petit,petits,petite,petites,grand,grande'
  ).split(',');

  function estFaible(m) { return FAIBLES.indexOf(m) !== -1; }

  /* Comparaison sur les cinq premières lettres : « fromages » retrouve
     « fromage », « magrets » retrouve « magret ». On compare préfixe à
     préfixe et jamais en sous-chaîne, sinon « entrecôte » attraperait
     « entrées ». */
  function memeMot(a, b) {
    if (a === b) return true;
    /* Six lettres, pas cinq : à cinq, « entrecôte » et « entrée »
       partagent « entre » et le carpaccio d'entrée remontait sur un
       Castillon. À six, « entrec » et « entree » se séparent, tandis que
       « fromages »/« fromage » et « magrets »/« magret » se retrouvent. */
    if (a.length < 6 || b.length < 6) return false;
    return a.slice(0, 6) === b.slice(0, 6);
  }

  /* ---------- Chargement de l'index, mis en cache pour la visite ---------- */
  var enCours = {};

  function chargerBlog(handle) {
    if (enCours[handle]) return enCours[handle];

    var cle = 'cl-recettes-' + handle;
    try {
      var cache = sessionStorage.getItem(cle);
      if (cache) {
        enCours[handle] = Promise.resolve(JSON.parse(cache));
        return enCours[handle];
      }
    } catch (e) { /* navigation privée : on rechargera, sans bruit */ }

    enCours[handle] = (function () {
      var tout = [];
      function page(n) {
        return fetch('/blogs/' + handle + '?view=index-json&page=' + n, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (txt) {
            if (!txt) return tout;
            var d;
            try { d = JSON.parse(txt); } catch (e) { return tout; }
            tout = tout.concat(d.articles || []);
            if (d.page < d.pages && n < 8) return page(n + 1);
            return tout;
          });
      }
      return page(1).then(function (liste) {
        try { sessionStorage.setItem(cle, JSON.stringify(liste)); } catch (e) { /* quota : tant pis */ }
        return liste;
      });
    })();

    return enCours[handle];
  }

  /* `recette.produits` arrive tantôt en tableau, tantôt en chaîne JSON. */
  function produitsDe(rec) {
    var p = rec.produits;
    if (!p) return [];
    if (Array.isArray(p)) return p;
    try { var j = JSON.parse(p); return Array.isArray(j) ? j : []; } catch (e) { return []; }
  }

  /* ---------- Notation ---------- */
  function noterCocktail(rec, base, handleProduit) {
    if (produitsDe(rec).indexOf(handleProduit) !== -1) return 100;
    if (!base) return 0;
    var b = normaliser(base);
    var tags = normaliser(rec.tags || '');
    var titre = normaliser(rec.titre || '');
    if ((' ' + tags + ' ').indexOf(' ' + b + ' ') !== -1) return 50;
    if ((' ' + titre + ' ').indexOf(' ' + b + ' ') !== -1) return 30;
    return 0;
  }

  /* Un accord n'est retenu que s'il repose sur au moins UN vrai nom de
     plat : « agneau », « canard », « fromages ». Une simple concordance
     de cuisson ne suffit pas — elle ajoute du poids, jamais l'accord. */
  /* Cocktail proposé sur un NON-spiritueux : il doit contenir vraiment
     l'ingrédient de la fiche. Les ingrédients priment sur les étiquettes,
     saisies de façon trop variable (« Champagne », « bulles »…). */
  function noterCocktailMotifs(rec, motifs, handleProduit) {
    var ingr = normaliser(rec.ingr || '');
    var ailleurs = normaliser((rec.tags || '') + ' ' + (rec.titre || ''));
    var note = 0;
    for (var i = 0; i < motifs.length; i++) {
      var m = normaliser(motifs[i]);
      if (!m) continue;
      if (ingr.indexOf(m) !== -1) { note = 40; break; }
      if (ailleurs.indexOf(m) !== -1) { note = 20; }
    }
    if (!note) return 0;
    if (produitsDe(rec).indexOf(handleProduit) !== -1) note += 100;
    return note;
  }

  /* Sur une boisson sans alcool, uniquement des cocktails sans alcool. */
  function noterMocktail(rec, handleProduit) {
    var t = normaliser((rec.tags || '') + ' ' + (rec.categorie || '') + ' ' + (rec.titre || ''));
    var sansAlcool = t.indexOf('sans alcool') !== -1 || t.indexOf('mocktail') !== -1 || t.indexOf('virgin') !== -1;
    if (!sansAlcool) return 0;
    return produitsDe(rec).indexOf(handleProduit) !== -1 ? 130 : 30;
  }

  function noterPlat(rec, motsAccords, handleProduit) {
    var cite = produitsDe(rec).indexOf(handleProduit) !== -1 ? 100 : 0;
    var motsTitre = normaliser(rec.titre || '').split(' ');
    var fort = 0, faible = 0;
    motsAccords.forEach(function (ma) {
      for (var i = 0; i < motsTitre.length; i++) {
        if (memeMot(ma, motsTitre[i])) {
          if (estFaible(ma) || estFaible(motsTitre[i])) { faible++; } else { fort++; }
          return;
        }
      }
    });
    /* Sans citation ET sans vrai nom de plat, aucun accord. */
    if (!cite && !fort) return 0;
    /* La note s'ADDITIONNE : parmi les recettes qui citent la bouteille,
       celles qui parlent aussi de ses accords passent devant. Sans cela,
       un Castillon proposait « Aubergines à la parmigiana » en premier,
       simple effet de l'ordre alphabétique. */
    return cite + fort * 10 + faible;
  }

  function meilleures(liste, noteur, nb) {
    return liste
      .map(function (r) { return { r: r, note: noteur(r) }; })
      .filter(function (x) { return x.note > 0; })
      .sort(function (a, b) { return b.note - a.note || (a.r.titre || '').localeCompare(b.r.titre || ''); })
      .slice(0, nb)
      .map(function (x) { return x.r; });
  }

  /* ---------- Rendu ---------- */
  function echapper(t) {
    return (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function carte(rec) {
    var meta = [];
    if (rec.temps) meta.push(echapper(rec.temps));
    if (rec.difficulte) meta.push(echapper(rec.difficulte));
    return ''
      + '<a class="clprec__carte" href="' + echapper(rec.url) + '">'
      + (rec.image
        ? '<span class="clprec__media"><img src="' + echapper(rec.image) + '" alt="' + echapper(rec.alt) + '" loading="lazy" decoding="async" width="500" height="375"></span>'
        : '<span class="clprec__media clprec__media--vide"></span>')
      + '<span class="clprec__texte">'
      + (rec.categorie ? '<span class="clprec__cat">' + echapper(rec.categorie) + '</span>' : '')
      + '<span class="clprec__titre">' + echapper(rec.titre) + '</span>'
      + (meta.length ? '<span class="clprec__meta">' + meta.join(' · ') + '</span>' : '')
      + '</span></a>';
  }

  function bloc(eyebrow, titre, recettes, lien, libelle) {
    if (!recettes.length) return '';
    return ''
      + '<div class="clprec__bloc">'
      + '<div class="clprec__head">'
      + (eyebrow ? '<p class="clprec__eyebrow">' + echapper(eyebrow) + '</p>' : '')
      + (titre ? '<h2 class="clprec__h2">' + echapper(titre) + '</h2>' : '')
      + '</div>'
      + '<div class="clprec__carrousel" data-carrousel>'
      + '<button class="clprec__fleche clprec__fleche--prec" type="button" aria-label="Recette précédente"></button>'
      + '<div class="clprec__piste" data-piste tabindex="0" role="group" aria-label="' + echapper(titre || 'Recettes') + '">'
      + recettes.map(carte).join('')
      + '</div>'
      + '<button class="clprec__fleche clprec__fleche--suiv" type="button" aria-label="Recette suivante"></button>'
      + '</div>'
      + (lien ? '<p class="clprec__plus"><a class="clprec__lien btn-lien" href="' + echapper(lien) + '">' + echapper(libelle || 'Voir toutes nos recettes') + ' →</a></p>' : '')
      + '</div>';
  }

  /* ---------- Carrousel ----------
     Défilement natif (scroll-snap) plutôt qu'une mécanique de
     transformations : la molette, le doigt et le clavier fonctionnent
     sans une ligne de plus, et rien ne casse si le script échoue.

     Le défilement automatique s'arrête dès que le visiteur survole,
     touche ou met le focus dans le carrousel — on ne fait pas défiler
     une carte sous le doigt de quelqu'un qui la lit. Il est également
     désactivé si le système demande de réduire les animations. */
  var DELAI = 4500;

  function activerCarrousels(racine) {
    var lents = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    Array.prototype.forEach.call(racine.querySelectorAll('[data-carrousel]'), function (car) {
      var piste = car.querySelector('[data-piste]');
      var prec = car.querySelector('.clprec__fleche--prec');
      var suiv = car.querySelector('.clprec__fleche--suiv');
      if (!piste) return;

      function pas() {
        var c = piste.firstElementChild;
        if (!c) return 300;
        var style = getComputedStyle(piste);
        return c.getBoundingClientRect().width + (parseFloat(style.columnGap || style.gap) || 0);
      }
      function debordement() { return piste.scrollWidth - piste.clientWidth; }

      /* Toutes les cartes tiennent à l'écran : ni flèches ni défilement. */
      if (debordement() < 8) {
        car.classList.add('clprec__carrousel--fixe');
        return;
      }

      function aller(sens) {
        var max = debordement();
        var cible = piste.scrollLeft + sens * pas();
        if (cible > max - 4) cible = sens > 0 ? 0 : max;   /* boucle */
        if (cible < 0) cible = max;
        piste.scrollTo({ left: cible, behavior: 'smooth' });
      }

      prec && prec.addEventListener('click', function () { aller(-1); relancer(); });
      suiv && suiv.addEventListener('click', function () { aller(1); relancer(); });

      function majFleches() {
        var max = debordement();
        car.classList.toggle('clprec__carrousel--debut', piste.scrollLeft < 8);
        car.classList.toggle('clprec__carrousel--fin', piste.scrollLeft > max - 8);
      }
      piste.addEventListener('scroll', majFleches, { passive: true });
      majFleches();

      if (lents) return;

      var minuteur = null;
      function demarrer() { if (!minuteur) minuteur = setInterval(function () { aller(1); }, DELAI); }
      function arreter() { if (minuteur) { clearInterval(minuteur); minuteur = null; } }
      function relancer() { arreter(); demarrer(); }

      ['pointerenter', 'focusin', 'touchstart'].forEach(function (e) {
        car.addEventListener(e, arreter, { passive: true });
      });
      ['pointerleave', 'focusout'].forEach(function (e) {
        car.addEventListener(e, demarrer);
      });
      /* Onglet en arrière-plan : inutile de faire tourner un minuteur. */
      document.addEventListener('visibilitychange', function () {
        document.hidden ? arreter() : demarrer();
      });

      demarrer();
    });
  }

  /* ---------- Mise en route ---------- */
  function remplir(el) {
    var handleProduit = el.getAttribute('data-produit');
    var base = el.getAttribute('data-base') || '';
    var accords = el.getAttribute('data-accords') || '';
    var nb = parseInt(el.getAttribute('data-nb'), 10) || 4;
    var motsAccords = motsUtiles(accords);
    var inner = el.querySelector('.clprec__inner');

    var motifs = (el.getAttribute('data-motifs') || '').split('|').filter(Boolean);
    var mocktail = el.getAttribute('data-mocktail') === 'true';

    function blocCocktails(noteur) {
      return chargerBlog(BLOG_COCKTAILS).then(function (liste) {
        return bloc(
          el.getAttribute('data-eyebrow-cocktails'),
          el.getAttribute('data-titre-cocktails'),
          meilleures(liste, noteur, nb),
          el.getAttribute('data-lien-cocktails'),
          el.getAttribute('data-libelle-lien')
        );
      });
    }
    function blocCuisine() {
      return chargerBlog(BLOG_CUISINE).then(function (liste) {
        return bloc(
          el.getAttribute('data-eyebrow-cuisine'),
          el.getAttribute('data-titre-cuisine'),
          meilleures(liste, function (r) { return noterPlat(r, motsAccords, handleProduit); }, nb),
          el.getAttribute('data-lien-cuisine'),
          el.getAttribute('data-libelle-lien')
        );
      });
    }

    /* Ce que chaque famille reçoit :
       - spiritueux : des cocktails, et rien d'autre ;
       - sans alcool : des mocktails, et rien d'autre ;
       - vin et effervescent : la cuisine D'ABORD, puis les seuls cocktails
         qui contiennent vraiment du vin ou des bulles ;
       - épicerie : la cuisine seule. */
    var travail;
    if (base) {
      travail = blocCocktails(function (r) { return noterCocktail(r, base, handleProduit); });
    } else if (mocktail) {
      travail = blocCocktails(function (r) { return noterMocktail(r, handleProduit); });
    } else if (accords && motifs.length) {
      travail = Promise.all([
        blocCuisine(),
        blocCocktails(function (r) { return noterCocktailMotifs(r, motifs, handleProduit); })
      ]).then(function (parts) { return parts.join(''); });
    } else if (accords) {
      travail = blocCuisine();
    } else if (motifs.length) {
      travail = blocCocktails(function (r) { return noterCocktailMotifs(r, motifs, handleProduit); });
    } else {
      return;
    }

    travail.then(function (html) {
      if (!html) return;            /* aucun accord trouvé : la section reste absente */
      inner.innerHTML = html;
      el.classList.add('clprec--pret');
      activerCarrousels(inner);
    }).catch(function () { /* la fiche reste telle quelle, sans section vide */ });
  }

  function demarrer() {
    var els = document.querySelectorAll('[data-cl-recettes]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, remplir);
      return;
    }
    var obs = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (e) {
        if (!e.isIntersecting) return;
        obs.unobserve(e.target);
        remplir(e.target);
      });
    }, { rootMargin: '600px 0px' });
    Array.prototype.forEach.call(els, function (el) { obs.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
